// FO-139 (batch-27) — Overeenkomsten. Gedeelde faserings-/redenlaag + de klant-
// tekenen-contracten (publieke tekenlink). Eén generator, twee domeinen: klant =
// ClientContract, arbeid = EmploymentContract. De commerciele `fase` staat NAAST
// de technische status van elk domeinrecord (niet door elkaar).
//
// Identiteitsscheiding is hard: klant-tekenen loopt via een Client/ClientPortalUser
// + tekenlink-token en legt een `ClientDocumentSignature` vast - NOOIT via
// `DocumentSignature.employeeId` (CLAUDE.md: `User` en `ClientPortalUser` worden
// nooit samengevoegd).

import { z } from 'zod'
import {
  clientContractBase,
  type ClientContractType,
  type ClientContractStatus,
} from './client-contract'
import type { GeneratedDocStatus } from './client-document-templates'
import type { DocumentTemplateCategory } from './document-templates'
// batch-62 briefpapier (FO-459) - de briefpapierkeuze bij het genereren.
import { BriefpapierKeuzeInvoerSchema } from './briefpapier'

// ─── Bruikbare sjabloon-categorieën voor een klantovereenkomst ───────────────

/**
 * FO-139 §6 - welke sjabloon-categorieën (binnen scope RELATIE) een klant-
 * overeenkomst mogen voeden. Eén bron voor de API-gate (genereer-service) en de
 * sjabloon-filter in de backoffice-wizard, zodat een nieuw type niet half
 * doorgevoerd kan raken (FO-400: PROJECTOVEREENKOMST).
 */
export const KLANT_OVEREENKOMST_CATEGORIEEN: readonly DocumentTemplateCategory[] = [
  'HOOFDOVEREENKOMST',
  'OPDRACHTBEVESTIGING',
  'PROJECTOVEREENKOMST',
] as const

// ─── OvereenkomstFase (commercieel) ──────────────────────────────────────────

export const OVEREENKOMST_FASES = ['AANBIEDING', 'VOORSTEL', 'OVEREENKOMST'] as const
export type OvereenkomstFase = (typeof OVEREENKOMST_FASES)[number]

export const OVEREENKOMST_FASE_LABELS: Record<OvereenkomstFase, string> = {
  AANBIEDING:   'Aanbieding',
  VOORSTEL:     'Voorstel',
  OVEREENKOMST: 'Overeenkomst',
}

// ─── OvereenkomstReden ───────────────────────────────────────────────────────

export const OVEREENKOMST_REDENEN = [
  'NIEUWE_MEDEWERKER',
  'NIEUWE_FUNCTIE',
  'CONTRACTVERLENGING',
  'SALARISVERHOGING',
  'URENWIJZIGING',
  'FUNCTIEWIJZIGING',
  'ONBEPAALDE_TIJD',
  'CORRECTIE',
  'OVERIG',
] as const
export type OvereenkomstReden = (typeof OVEREENKOMST_REDENEN)[number]

export const OVEREENKOMST_REDEN_LABELS: Record<OvereenkomstReden, string> = {
  NIEUWE_MEDEWERKER:  'Nieuwe medewerker',
  NIEUWE_FUNCTIE:     'Nieuwe functie',
  CONTRACTVERLENGING: 'Contractverlenging',
  SALARISVERHOGING:   'Salarisverhoging',
  URENWIJZIGING:      'Urenwijziging',
  FUNCTIEWIJZIGING:   'Functiewijziging',
  ONBEPAALDE_TIJD:    'Omzetten naar onbepaalde tijd',
  CORRECTIE:          'Correctie',
  OVERIG:             'Overig',
}

// ─── Klant-overeenkomst Zod-schemas (gedeeld API + form) ─────────────────────
//
// De klantovereenkomst hangt aan `ClientContract` (leidend). We hergebruiken de
// FO-093 `clientContractBase` (één bron voor titel/type/datums/bedrag) en voegen
// alleen de Overeenkomsten-laag toe (fase/reden/template/ondertekening).

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// Klantovereenkomsten voeren alleen de `fase` (commercieel) + template/ondertekening
// toe; `reden` is een arbeid-concept en zit alleen op EmploymentContract.
const overeenkomstExtraFields = {
  fase: z.enum(OVEREENKOMST_FASES).optional(),
  templateId: z.string().min(1).nullable().optional(),
  ondertekeningVerplicht: z.boolean().optional(),
}

// einddatum mag niet vóór ingangsdatum liggen (jjjj-mm-dd vergelijkt chronologisch).
const einddatumNaIngang = (data: {
  ingangsdatum?: string
  einddatum?: string | null
}): boolean => {
  if (!data.ingangsdatum || !data.einddatum) return true
  return data.einddatum >= data.ingangsdatum
}
const einddatumError: { message: string; path: string[] } = {
  message: 'Einddatum mag niet vóór de ingangsdatum liggen',
  path: ['einddatum'],
}

export const CreateKlantOvereenkomstSchema = clientContractBase
  .extend(overeenkomstExtraFields)
  .refine(einddatumNaIngang, einddatumError)
export type CreateKlantOvereenkomstInput = z.infer<typeof CreateKlantOvereenkomstSchema>

export const UpdateKlantOvereenkomstSchema = clientContractBase
  .extend(overeenkomstExtraFields)
  .partial()
  .refine(einddatumNaIngang, einddatumError)
export type UpdateKlantOvereenkomstInput = z.infer<typeof UpdateKlantOvereenkomstSchema>

// Genereren: welk sjabloon + optionele BV-entiteit + de ingevulde merge-waarden.
export const OvereenkomstGenererenSchema = z.object({
  templateId: z.string().min(1, 'Kies een sjabloon.'),
  entityId: z.string().min(1).nullable().optional(),
  placeholderValues: z.record(z.string(), z.string()).optional(),
  /** FO-459 (batch-62) - briefpapierkeuze uit de kiezer; leeg = cascade. */
  briefpapier: BriefpapierKeuzeInvoerSchema.nullable().optional(),
})
export type OvereenkomstGenererenInput = z.infer<typeof OvereenkomstGenererenSchema>

// Vrijgeven voor de klant: optioneel direct een tekenlink mailen naar een
// contactpersoon/e-mailadres.
export const OvereenkomstVrijgevenSchema = z.object({
  verzendTekenlink: z.boolean().optional(),
  email: z.string().trim().email('Geef een geldig e-mailadres op.').optional().or(z.literal('')),
  contactId: z.string().min(1).nullable().optional(),
  contactNaam: z.string().trim().max(160).optional(),
})
export type OvereenkomstVrijgevenInput = z.infer<typeof OvereenkomstVrijgevenSchema>

// Handmatig als getekend markeren (buiten de digitale flow om, bv. natte
// handtekening op papier). Legt naam + optioneel datum vast.
export const OvereenkomstMarkeerGetekendSchema = z.object({
  naam: z.string().trim().min(1, 'Naam is verplicht.').max(160),
  getekendOp: z.string().regex(ISO_DATE, 'Geef een geldige datum op.').optional(),
})
export type OvereenkomstMarkeerGetekendInput = z.infer<typeof OvereenkomstMarkeerGetekendSchema>

// ─── Klant-tekenen (publieke tekenlink) ──────────────────────────────────────
//
// De klant tekent via een beveiligde link met token (geen login vereist). De
// publieke payload lekt NOOIT interne data (kostprijs/marge/andere klanten): enkel
// de documenttitel, de klantnaam en de fase.

export interface OvereenkomstTekenPayload {
  documentTitel: string
  clientNaam: string
  fase: OvereenkomstFase
  faseLabel: string
  /** Relatieve download-URL van de PDF (via de token-poort). */
  downloadPdfUrl: string
  /** Relatieve download-URL van de Word-versie, of null als er geen DOCX is. */
  downloadDocxUrl: string | null
  reedsGetekend: boolean
  getekendDoor: string | null
  getekendOp: string | null
  expiresAt: string
}

export const OvereenkomstTekenSchema = z.object({
  naam: z.string().trim().min(2, 'Vul uw naam in.').max(160),
  functie: z.string().trim().max(160).optional(),
  email: z.string().trim().email('Vul een geldig e-mailadres in.').optional().or(z.literal('')),
  // base64 PNG data-URL van het handtekening-canvas.
  signatureData: z.string().min(50, 'Plaats uw handtekening.').max(1_000_000),
})
export type OvereenkomstTekenInput = z.infer<typeof OvereenkomstTekenSchema>

// ─── Read-DTO (klant-overeenkomst = ClientContract + gegenereerd document) ───

export interface OvereenkomstGeneratedInfo {
  generatedDocumentId: string
  documentId: string
  fileName: string
  status: GeneratedDocStatus
  statusLabel: string
  heeftPdf: boolean
  heeftDocx: boolean
  createdAt: string
}

export interface KlantOvereenkomstDTO {
  id: string
  clientId: string
  titel: string
  type: ClientContractType
  typeLabel: string
  status: ClientContractStatus
  statusLabel: string
  fase: OvereenkomstFase
  faseLabel: string
  ingangsdatum: string
  einddatum: string | null
  looptijdMaanden: number | null
  opzegtermijnDagen: number | null
  /** Decimal-strings (nooit Float). */
  contractbedrag: string | null
  indexeringPct: string | null
  responsibleEmployeeId: string | null
  responsibleEmployeeNaam: string | null
  notitie: string | null
  templateId: string | null
  ondertekeningVerplicht: boolean
  vrijgegevenOp: string | null
  /** Dagen tot einddatum (Europe/Amsterdam); negatief = verlopen; null = geen einddatum. */
  dagenTotEinde: number | null
  /** Het gekoppelde gegenereerde document (het document IS de versie). */
  generated: OvereenkomstGeneratedInfo | null
  /** Laatste handmatig geüploade contract-versie (FO-093), voor download als er
   *  (nog) geen gegenereerd document is. Null als er geen versie-upload is. */
  laatsteVersie: { documentId: string; documentVersieNr: number; versieNr: number } | null
  /** De klant-handtekening (indien getekend); nooit een medewerker. */
  getekendDoorKlant: { naam: string; getekendOp: string } | null
  /** Er staat een geldige (niet-verlopen) tekenlink open. */
  tekenlinkActief: boolean
  createdAt: string
  updatedAt: string
}
